import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import api from '@/services/api';
import { useAuth } from '@/context/AuthContext';
import { Eye, EyeOff, Loader2, ArrowLeft } from 'lucide-react';

export const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { loginWithEmail, register, logout, user, isAuthenticated, loading: authLoading } = useAuth();
  // Referral link support: /signup?aff=<code> lands users on the Sign Up
  // tab with the affiliate code captured so the account is credited on
  // registration.
  const isSignupPath = location.pathname === '/signup' || location.pathname === '/register';
  const urlAffParam = searchParams.get('aff') || searchParams.get('ref') || searchParams.get('referral') || '';
  // Referral persistence: if the visitor lands on ANY page with ?aff=<code>
  // (marketing home, blog, pricing…), a global effect elsewhere writes it
  // to localStorage. When they eventually reach /signup, we pick it back
  // up so the account is properly attributed even after several page nav
  // hops (fixes iter124 bug #14 — referrals disappearing during navigation).
  const [persistedAff] = useState(() => {
    try { return localStorage.getItem('harmonix_ref_code') || ''; }
    catch { return ''; }
  });
  const affParam = urlAffParam || persistedAff;
  const [activeTab, setActiveTab] = useState(
    (isSignupPath || searchParams.get('signup') || affParam) ? 'signup' : 'signin'
  );
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [switchingAccount, setSwitchingAccount] = useState(false);

  const isSignUp = activeTab === 'signup';
  const pageTitle = isSignUp
    ? 'Create your account · Harmonix'
    : 'Sign in · Harmonix';
  const pageDescription = isSignUp
    ? 'Create your Harmonix account and deploy your first AI Employee in minutes.'
    : 'Sign in to Harmonix to manage your AI Employees, leads, and integrations.';

  // Form states
  const [signInData, setSignInData] = useState({ email: '', password: '' });
  const [signUpData, setSignUpData] = useState({ name: '', email: '', password: '', confirmPassword: '' });

  // If already authenticated, we no longer silently redirect — the user
  // explicitly clicked "Login", so show them who they're signed in as and
  // let them either continue to the dashboard or sign out first. This
  // matches user-reported feedback that the silent redirect felt like the
  // page had auto-logged them in.
  const alreadyLoggedIn = !authLoading && isAuthenticated && !switchingAccount;

  // React-Helmet-Async can't remove the static <meta name="robots"> we ship
  // in public/index.html (that tag sets 'index, follow' for the marketing
  // site). On the auth pages we do NOT want Google to index the sign-in
  // form, so patch the existing tag imperatively while this page is mounted
  // and restore it on unmount.
  useEffect(() => {
    const el = document.querySelector('meta[name="robots"]:not([data-rh])');
    if (!el) return undefined;
    const original = el.getAttribute('content');
    el.setAttribute('content', 'noindex, nofollow');
    return () => {
      if (original !== null) el.setAttribute('content', original);
    };
  }, []);

  // Surface a friendly toast whenever the user arrives here after cancelling
  // an OAuth/SSO consent flow. Backend redirects with ?auth_error=sso_cancelled
  // (or =calendar_cancelled) instead of returning a raw 422 JSON payload.
  useEffect(() => {
    const err = searchParams.get('auth_error');
    if (!err) return;
    const map = {
      sso_cancelled: 'Google sign-in was cancelled. Please try again.',
      sso_error: 'Sign-in could not be completed. Please try again.',
    };
    toast.error(map[err] || 'Sign-in could not be completed. Please try again.');
    // Strip the flag so a refresh doesn't re-toast.
    const url = new URL(window.location.href);
    url.searchParams.delete('auth_error');
    window.history.replaceState({}, '', url.toString());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleContinueToDashboard = () => navigate('/dashboard', { replace: true });
  const handleSignOutAndSwitch = async () => {
    setSwitchingAccount(true);
    try {
      await logout();
    } catch (_) {
      // Ignore; the local session is cleared either way.
    }
  };

  // Handle Google OAuth - uses Emergent Auth
  const handleGoogleAuth = () => {
    const redirectUrl = `${window.location.origin}/dashboard`;
    const authUrl = new URL('https://auth.emergentagent.com/');
    authUrl.searchParams.set('redirect', redirectUrl);
    authUrl.searchParams.set('app_name', 'Harmonix');
    window.location.href = authUrl.toString();
  };

  // Handle Sign In — uses the shared `api` instance so withCredentials:true
  // is always set and the httpOnly session cookie is stored by the browser.
  const handleSignIn = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await loginWithEmail(signInData.email, signInData.password);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Login error:', error);
      let message;
      if (error.message === 'Network Error' || !error.response) {
        message = "Couldn't reach the server — please check your internet connection and try again.";
      } else if (error.response?.status === 403 && /suspend/i.test(error.response?.data?.detail || '')) {
        message = 'Your account has been suspended. Please contact support.';
      } else {
        message = error.response?.data?.detail || 'Invalid email or password';
      }
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Handle Sign Up
  const handleSignUp = async (e) => {
    e.preventDefault();
    
    if (signUpData.password !== signUpData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    // Password validation
    const password = signUpData.password;
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (!/[A-Z]/.test(password)) {
      toast.error('Password must contain at least one uppercase letter');
      return;
    }
    if (!/[a-z]/.test(password)) {
      toast.error('Password must contain at least one lowercase letter');
      return;
    }
    if (!/[0-9]/.test(password)) {
      toast.error('Password must contain at least one number');
      return;
    }
    if (!/[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\;'`~]/.test(password)) {
      toast.error('Password must contain at least one special character');
      return;
    }
    
    setLoading(true);
    
    try {
      await register(signUpData.name, signUpData.email, signUpData.password, 'customer', affParam || null);
      toast.success(affParam ? 'Account created! Referral applied.' : 'Account created successfully!');
      // Clear the persisted referral code so subsequent signups from the
      // same device aren't attributed to the same affiliate by mistake.
      try { localStorage.removeItem('harmonix_ref_code'); } catch { /* ignore */ }
      navigate('/dashboard');
    } catch (error) {
      let message;
      if (error.message === 'Network Error' || !error.response) {
        message = "Couldn't reach the server — please check your internet connection and try again.";
      } else {
        message = error.response?.data?.detail || 'Failed to create account';
      }
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Short-circuit render: if the user is already signed in, don't auto-redirect
  // (previous behavior). Instead show an explicit "You're signed in as X" panel
  // so they can decide whether to continue or switch accounts.
  if (alreadyLoggedIn) {
    return (
      <>
        <Helmet>
          <title>Already signed in · Harmonix</title>
          <meta name="description" content="You are already signed in to Harmonix." />
        </Helmet>
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-teal-50 px-4">
          <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 shadow-xl p-8 space-y-5" data-testid="already-signed-in-panel">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center text-white text-lg font-semibold">
                {(user?.name || user?.email || 'H').slice(0, 1).toUpperCase()}
              </div>
              <div className="min-w-0">
                <p className="text-sm text-slate-500">You&apos;re already signed in as</p>
                <p className="text-base font-semibold text-slate-900 truncate">{user?.email || user?.name}</p>
              </div>
            </div>
            <p className="text-sm text-slate-600">
              We didn&apos;t auto-log you in — this is your existing session. You can continue to your dashboard, or sign out and log in with a different account.
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                onClick={handleContinueToDashboard}
                className="flex-1 bg-teal-600 hover:bg-teal-700"
                data-testid="continue-to-dashboard-btn"
              >
                Continue to dashboard
              </Button>
              <Button
                variant="outline"
                onClick={handleSignOutAndSwitch}
                className="flex-1 border-slate-300 text-slate-700"
                data-testid="sign-out-and-switch-btn"
              >
                Sign out &amp; switch account
              </Button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        {/* Auth pages should not be indexed by search engines. */}
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
    <div className="min-h-screen flex">
      {/* Left Side - Brand Visual */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-gradient-to-br from-teal-500 to-cyan-600">
        {/* Abstract pattern overlay */}
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <defs>
              <pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse">
                <path d="M 10 0 L 0 0 0 10" fill="none" stroke="white" strokeWidth="0.5"/>
              </pattern>
            </defs>
            <rect width="100" height="100" fill="url(#grid)" />
          </svg>
        </div>
        
        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center px-12 xl:px-20">
          <div className="mb-8">
            <h1 className="text-4xl xl:text-5xl font-extrabold text-white tracking-tight mb-4">
              Harmonix
            </h1>
            <p className="text-xl text-white/90 font-medium">
              Your AI Employee Platform
            </p>
          </div>
          
          <div className="space-y-6 text-white/80">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p>Respond to every message instantly, 24/7</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p>Book appointments automatically</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p>Capture and qualify leads on autopilot</p>
            </div>
          </div>
          
          <div className="mt-12 pt-8 border-t border-white/20">
            <p className="text-sm text-white/60">
              Trusted by 1,000+ service businesses worldwide
            </p>
          </div>
        </div>
      </div>
      
      {/* Right Side - Auth Form */}
      <div className="w-full lg:w-1/2 flex flex-col">
        {/* Back to Home Link */}
        <div className="p-6">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-slate-500 hover:text-slate-700 text-sm font-medium transition-colors"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </button>
        </div>
        
        {/* Form Container */}
        <div className="flex-1 flex items-center justify-center px-6 py-12">
          <div className="w-full max-w-[400px]">
            {/* Mobile Logo */}
            <div className="lg:hidden text-center mb-8">
              <h1 className="text-2xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-teal-500 to-cyan-500">
                Harmonix
              </h1>
            </div>
            
            {/* Welcome Text */}
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight">
                {activeTab === 'signin' ? 'Welcome back' : 'Create your account'}
              </h2>
              <p className="mt-2 text-slate-500">
                {activeTab === 'signin' 
                  ? 'Sign in to access your AI employees' 
                  : 'Start automating your business today'}
              </p>
            </div>
            
            {/* Auth Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6 bg-slate-100 p-1 rounded-lg h-11">
                <TabsTrigger 
                  value="signin" 
                  className="rounded-md text-sm font-medium data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm"
                  data-testid="signin-tab"
                >
                  Sign In
                </TabsTrigger>
                <TabsTrigger 
                  value="signup"
                  className="rounded-md text-sm font-medium data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm"
                  data-testid="signup-tab"
                >
                  Sign Up
                </TabsTrigger>
              </TabsList>
              
              {/* Sign In Form */}
              <TabsContent value="signin">
                <form onSubmit={handleSignIn} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signin-email" className="text-sm font-medium text-slate-700">
                      Email
                    </Label>
                    <Input
                      id="signin-email"
                      type="email"
                      placeholder="you@example.com"
                      value={signInData.email}
                      onChange={(e) => setSignInData({ ...signInData, email: e.target.value })}
                      required
                      className="h-11"
                      data-testid="signin-email-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="signin-password" className="text-sm font-medium text-slate-700">
                        Password
                      </Label>
                      <button 
                        type="button"
                        onClick={() => navigate('/forgot-password')}
                        className="text-sm text-teal-600 hover:text-teal-700 font-medium"
                        data-testid="forgot-password-link"
                      >
                        Forgot password?
                      </button>
                    </div>
                    <div className="relative">
                      <Input
                        id="signin-password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Enter your password"
                        value={signInData.password}
                        onChange={(e) => setSignInData({ ...signInData, password: e.target.value })}
                        required
                        className="h-11 pr-10"
                        data-testid="signin-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full h-11 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white font-medium"
                    disabled={loading}
                    data-testid="signin-submit-button"
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      'Sign In'
                    )}
                  </Button>
                </form>
              </TabsContent>
              
              {/* Sign Up Form */}
              <TabsContent value="signup">
                <form onSubmit={handleSignUp} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signup-name" className="text-sm font-medium text-slate-700">
                      Full Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="signup-name"
                      type="text"
                      placeholder="John Doe"
                      value={signUpData.name}
                      onChange={(e) => setSignUpData({ ...signUpData, name: e.target.value })}
                      required
                      className="h-11"
                      data-testid="signup-name-input"
                      autoComplete="off"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="signup-email" className="text-sm font-medium text-slate-700">
                      Email <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="signup-email"
                      type="email"
                      placeholder="you@example.com"
                      value={signUpData.email}
                      onChange={(e) => setSignUpData({ ...signUpData, email: e.target.value })}
                      required
                      className="h-11"
                      data-testid="signup-email-input"
                      autoComplete="off"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="signup-password" className="text-sm font-medium text-slate-700">
                      Password <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                      <Input
                        id="signup-password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="8+ chars, uppercase, lowercase, number, special"
                        value={signUpData.password}
                        onChange={(e) => setSignUpData({ ...signUpData, password: e.target.value })}
                        required
                        minLength={8}
                        className="h-11 pr-10"
                        data-testid="signup-password-input"
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="signup-confirm" className="text-sm font-medium text-slate-700">
                      Confirm Password <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="signup-confirm"
                      type="password"
                      placeholder="Confirm your password"
                      value={signUpData.confirmPassword}
                      onChange={(e) => setSignUpData({ ...signUpData, confirmPassword: e.target.value })}
                      required
                      className="h-11"
                      data-testid="signup-confirm-input"
                    />
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full h-11 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white font-medium"
                    disabled={loading}
                    data-testid="signup-submit-button"
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      'Create Account'
                    )}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
            
            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-slate-400">or continue with</span>
              </div>
            </div>
            
            {/* Google OAuth */}
            <Button
              type="button"
              variant="outline"
              onClick={handleGoogleAuth}
              className="w-full h-11 border-slate-200 text-slate-700 hover:bg-slate-50 font-medium"
              data-testid="google-oauth-button"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Continue with Google
            </Button>
            
            {/* Terms */}
            <p className="mt-6 text-center text-xs text-slate-400">
              By continuing, you agree to our{' '}
              <a href="/terms" className="text-teal-600 hover:text-teal-700">Terms of Service</a>
              {' '}and{' '}
              <a href="/privacy" className="text-teal-600 hover:text-teal-700">Privacy Policy</a>
            </p>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default LoginPage;
